import { DownloadOutlined, EditOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import { parse as parseContentDisposition } from "@tinyhttp/content-disposition";
import { App, Badge, Button, Divider, Flex, Input, Select, Space, Spin, Table } from "antd";
import type { UploadProps } from "antd";
import Column from "antd/es/table/Column";
import type { SorterResult, TablePaginationConfig } from "antd/es/table/interface";
import dayjs from "dayjs";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CompanyContext, UpdateCompanyContext } from "../../../context/companyContext";
import { clientFetch } from "../../../utils/clientFetch";
import { notifyApiError, notifyHttpError } from "../../../utils/apiErrorHandler";
import {
  parseItemMasterCsvErrorPayload,
  showItemMasterCsvUploadErrorModal,
} from "./itemMasterCsvErrorModal";
import { PATH_INFO, shouldStopForInvalidContextFetchResult } from "../../util/helper";
import { CsvImportModalButton } from "../../../components/common/CsvImportModalButton";
import {
  COMPANY_ITEM_MASTER_DEFAULT_PAGE_SIZE,
  COMPANY_ITEM_MASTER_JAN_CODE_MATCH_TYPE_OPTIONS,
  COMPANY_ITEM_MASTER_MAX_TEXT_LENGTH,
  COMPANY_ITEM_MASTER_PAGE_SIZE_OPTIONS,
  CompanyItemMasterListResponse,
  CompanyItemMasterResponseType,
  CompanyItemMasterSearchFormValue,
  createDefaultCompanyItemMasterSearchForm,
  getCompanyItemMasterDisplayValue,
  normalizeCompanyItemMasterSearchForm,
  validateCompanyItemMasterJanCodeSearch,
} from "./itemMasterHelper";

type PaginationState = {
  current: number;
  pageSize: number;
  total: number;
};

type ApiErrorPayload = {
  message?: string;
};

type CompanyItemMasterFetchParams = {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  filters?: CompanyItemMasterSearchFormValue;
};

const COMPANY_ID_QUERY_PARAM = "companyId";

const normalizeCompanyIdQueryParam = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  return /^\d+$/.test(trimmed) ? trimmed : null;
};

const ITEM_MASTER_SEARCH_FIELDS: ReadonlyArray<{
  key: Exclude<keyof CompanyItemMasterSearchFormValue, "jan_code" | "jan_code_match_type">;
  labelKey: string;
  placeholderKey: string;
}> = [
    { key: "part_number", labelKey: "itemMaster.partNumber", placeholderKey: "itemMaster.partNumberSearchPlaceholder" },
    { key: "item_name", labelKey: "itemMaster.itemName", placeholderKey: "itemMaster.itemNameSearchPlaceholder" },
    { key: "brand_name", labelKey: "itemMaster.brandName", placeholderKey: "itemMaster.brandNameSearchPlaceholder" },
  ];

export function CompanyItemMaster() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { modal } = App.useApp();
  const { companyOptions } = useContext(CompanyContext);
  const { refreshCompany, refreshCompanyOptions } = useContext(UpdateCompanyContext);

  const companyId = useMemo(
    () => normalizeCompanyIdQueryParam(searchParams.get(COMPANY_ID_QUERY_PARAM)),
    [searchParams]
  );
  const [itemMasterList, setItemMasterList] = useState<CompanyItemMasterResponseType[]>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    current: 1,
    pageSize: COMPANY_ITEM_MASTER_DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [isPageReady, setIsPageReady] = useState(() => companyId === null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [currentSortField, setCurrentSortField] = useState<string | undefined>(undefined);
  const [currentSortOrder, setCurrentSortOrder] = useState<"ascend" | "descend" | undefined>(undefined);
  const [searchForm, setSearchForm] = useState<CompanyItemMasterSearchFormValue>(createDefaultCompanyItemMasterSearchForm);
  const [currentFilters, setCurrentFilters] = useState<CompanyItemMasterSearchFormValue>(createDefaultCompanyItemMasterSearchForm);
  const activeFetchControllerRef = useRef<AbortController | null>(null);
  const latestFetchRequestIdRef = useRef(0);
  const hasResolvedInitialViewRef = useRef(companyId === null);

  const companySelectOptions = useMemo(() => companyOptions.map((targetCompany) => ({
    value: String(targetCompany.id),
    label: targetCompany.company_name || t("itemMaster.companyIdLabel", { id: targetCompany.id }),
  })), [companyOptions, t]);

  const selectedCompanyName = useMemo(() => {
    return companySelectOptions.find((option) => option.value === companyId)?.label;
  }, [companyId, companySelectOptions]);

  const getItemMasterManagePath = useCallback((targetCompanyId: string, itemMasterId: string) => {
    return PATH_INFO.COMPANY_ITEM_MASTER_MANAGE.PATH
      .replace(":companyId", targetCompanyId)
      .replace(":itemMasterId", itemMasterId);
  }, []);

  const renderDateTime = useCallback((value: string | null) => {
    if (!value) {
      return "-";
    }

    return dayjs(value).format("YYYY/MM/DD HH:mm");
  }, []);

  const parseApiErrorMessage = useCallback(async (response: Response) => {
    try {
      const contentType = response.headers.get("content-type") ?? "";
      if (!contentType.includes("application/json")) {
        return undefined;
      }

      const payload = await response.clone().json() as ApiErrorPayload;
      if (typeof payload.message === "string" && payload.message.trim() !== "") {
        return payload.message;
      }
    } catch (error) {
      console.error("Failed to parse item master error response.", error);
    }

    return undefined;
  }, []);

  const toApiSortOrder = useCallback((value?: "ascend" | "descend") => {
    if (value === "ascend") {
      return "asc";
    }

    if (value === "descend") {
      return "desc";
    }

    return undefined;
  }, []);

  const fetchItemMasters = useCallback(
    async ({
      page = 1,
      pageSize = COMPANY_ITEM_MASTER_DEFAULT_PAGE_SIZE,
      sortField,
      sortOrder,
      filters = createDefaultCompanyItemMasterSearchForm(),
    }: CompanyItemMasterFetchParams = {}) => {
      if (!companyId) {
        activeFetchControllerRef.current?.abort();
        activeFetchControllerRef.current = null;
        setItemMasterList([]);
        setPagination({
          current: 1,
          pageSize,
          total: 0,
        });
        setIsLoading(false);
        return;
      }

      activeFetchControllerRef.current?.abort();
      const fetchController = new AbortController();
      activeFetchControllerRef.current = fetchController;
      const requestId = latestFetchRequestIdRef.current + 1;
      latestFetchRequestIdRef.current = requestId;

      setIsLoading(true);

      try {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: pageSize.toString(),
        });

        if (sortField && sortOrder) {
          params.append("sortField", sortField);
          params.append("sortOrder", sortOrder);
        }

        if (filters.jan_code) {
          params.append("jan_code", filters.jan_code);
          params.append("jan_code_match_type", filters.jan_code_match_type);
        }

        if (filters.part_number) {
          params.append("part_number", filters.part_number);
        }

        if (filters.item_name) {
          params.append("item_name", filters.item_name);
        }

        if (filters.brand_name) {
          params.append("brand_name", filters.brand_name);
        }

        const response = await clientFetch(
          `${process.env.REACT_APP_API_ENDPOINT}/apis/admin/company-item-masters/company/${companyId}?${params.toString()}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            signal: fetchController.signal,
          }
        );

        if (!response.ok) {
          notifyHttpError(response, { logMessage: "Error fetching company item masters." });
          if (requestId !== latestFetchRequestIdRef.current) {
            return;
          }
          setItemMasterList([]);
          setPagination({
            current: 1,
            pageSize,
            total: 0,
          });
          return;
        }

        const payload = await response.json() as CompanyItemMasterListResponse;
        const list = payload.data ?? [];
        const meta = payload.meta;

        if (fetchController.signal.aborted || requestId !== latestFetchRequestIdRef.current) {
          return;
        }

        setItemMasterList(list);
        setPagination({
          current: typeof meta?.page === "number" ? meta.page : page,
          pageSize: typeof meta?.limit === "number" ? meta.limit : pageSize,
          total: typeof meta?.total === "number" ? meta.total : list.length,
        });
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }

        notifyApiError({ error, logMessage: "Error fetching company item masters." });
        if (requestId !== latestFetchRequestIdRef.current) {
          return;
        }
        setItemMasterList([]);
        setPagination({
          current: 1,
          pageSize,
          total: 0,
        });
      } finally {
        if (activeFetchControllerRef.current === fetchController) {
          activeFetchControllerRef.current = null;
        }

        if (requestId === latestFetchRequestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [companyId]
  );

  useEffect(() => {
    let isCancelled = false;

    const loadCompanyOptions = async () => {
      setIsLoadingCompanies(true);

      try {
        await refreshCompanyOptions();
      } catch (error) {
        notifyApiError({ error, logMessage: "Error fetching company options for item master." });
      } finally {
        if (!isCancelled) {
          setIsLoadingCompanies(false);
        }
      }
    };

    loadCompanyOptions();

    return () => {
      isCancelled = true;
    };
  }, [refreshCompanyOptions]);

  useEffect(() => {
    let isCancelled = false;
    const defaultSearchForm = createDefaultCompanyItemMasterSearchForm();

    setItemMasterList([]);
    setPagination({
      current: 1,
      pageSize: COMPANY_ITEM_MASTER_DEFAULT_PAGE_SIZE,
      total: 0,
    });
    setCurrentSortField(undefined);
    setCurrentSortOrder(undefined);
    setSearchForm(defaultSearchForm);
    setCurrentFilters(defaultSearchForm);

    const validateAndLoadItemMasters = async () => {
      if (!companyId) {
        activeFetchControllerRef.current?.abort();
        activeFetchControllerRef.current = null;
        hasResolvedInitialViewRef.current = true;
        setIsPageReady(true);
        setIsLoading(false);
        return;
      }

      try {
        const companyResult = await refreshCompany(companyId);

        if (isCancelled) {
          return;
        }

        if (shouldStopForInvalidContextFetchResult(companyResult, navigate)) {
          return;
        }

        if (!hasResolvedInitialViewRef.current) {
          hasResolvedInitialViewRef.current = true;
          setIsPageReady(true);
        }
        await fetchItemMasters({
          page: 1,
          pageSize: COMPANY_ITEM_MASTER_DEFAULT_PAGE_SIZE,
          filters: defaultSearchForm,
        });
      } catch (error) {
        console.error("Error validating company item masters.", error);
        if (isCancelled) {
          return;
        }
        navigate(PATH_INFO.ERROR.PATH, { replace: true });
      }
    };

    validateAndLoadItemMasters();

    return () => {
      isCancelled = true;
      activeFetchControllerRef.current?.abort();
      activeFetchControllerRef.current = null;
    };
  }, [companyId, fetchItemMasters, navigate, refreshCompany]);

  const handleCompanyChange = useCallback((value?: string) => {
    const nextCompanyId = normalizeCompanyIdQueryParam(value);
    const nextSearchParams = new URLSearchParams(searchParams);

    if (nextCompanyId) {
      nextSearchParams.set(COMPANY_ID_QUERY_PARAM, nextCompanyId);
    } else {
      nextSearchParams.delete(COMPANY_ID_QUERY_PARAM);
    }

    if (nextCompanyId === companyId && nextSearchParams.toString() === searchParams.toString()) {
      return;
    }

    setSearchParams(nextSearchParams, { replace: true });
  }, [companyId, searchParams, setSearchParams]);

  const handleSearchInputChange = useCallback(
    (key: keyof CompanyItemMasterSearchFormValue, value: string) => {
      setSearchForm((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  const handleJanCodeInputChange = useCallback((value: string) => {
    setSearchForm((prev) => ({
      ...prev,
      jan_code: value,
    }));
  }, []);

  const handleJanCodeMatchTypeChange = useCallback((value: CompanyItemMasterSearchFormValue["jan_code_match_type"]) => {
    setSearchForm((prev) => ({
      ...prev,
      jan_code_match_type: value,
    }));
  }, []);

  const onSearch = useCallback(async () => {
    const normalizedFilters = normalizeCompanyItemMasterSearchForm(searchForm);
    setSearchForm(normalizedFilters);
    const janCodeValidation = validateCompanyItemMasterJanCodeSearch(normalizedFilters);
    if (!janCodeValidation.isValid) {
      return;
    }

    setCurrentFilters(normalizedFilters);
    await fetchItemMasters({
      page: 1,
      pageSize: pagination.pageSize,
      sortField: currentSortField,
      sortOrder: toApiSortOrder(currentSortOrder),
      filters: normalizedFilters,
    });
  }, [currentSortField, currentSortOrder, fetchItemMasters, pagination.pageSize, searchForm, toApiSortOrder]);

  const onClearSearch = useCallback(async () => {
    const defaultFilters = createDefaultCompanyItemMasterSearchForm();
    setSearchForm(defaultFilters);
    setCurrentFilters(defaultFilters);
    await fetchItemMasters({
      page: 1,
      pageSize: pagination.pageSize,
      sortField: currentSortField,
      sortOrder: toApiSortOrder(currentSortOrder),
      filters: defaultFilters,
    });
  }, [currentSortField, currentSortOrder, fetchItemMasters, pagination.pageSize, toApiSortOrder]);

  const handleTableChange = useCallback(
    (
      tablePagination: TablePaginationConfig,
      _filters: Record<string, unknown>,
      sorter: SorterResult<CompanyItemMasterResponseType> | SorterResult<CompanyItemMasterResponseType>[]
    ) => {
      const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
      const requestedPageSize = tablePagination.pageSize ?? pagination.pageSize;
      const requestedPage = tablePagination.current ?? pagination.current;

      let nextSortField: string | undefined;
      let nextSortOrder: "ascend" | "descend" | undefined;
      let nextApiSortOrder: "asc" | "desc" | undefined;

      if (activeSorter?.field && typeof activeSorter.field === "string" && activeSorter.order) {
        nextSortField = activeSorter.field;
        nextSortOrder = activeSorter.order === "ascend" ? "ascend" : "descend";
        nextApiSortOrder = nextSortOrder === "ascend" ? "asc" : "desc";
      }

      const hasSortChanged = currentSortField !== nextSortField || currentSortOrder !== nextSortOrder;
      const nextPage = hasSortChanged ? 1 : requestedPage;

      setCurrentSortField(nextSortField);
      setCurrentSortOrder(nextSortOrder);
      fetchItemMasters({
        page: nextPage,
        pageSize: requestedPageSize,
        sortField: nextSortField,
        sortOrder: nextApiSortOrder,
        filters: currentFilters,
      });
    },
    [currentFilters, currentSortField, currentSortOrder, fetchItemMasters, pagination]
  );

  const onClickManage = useCallback((id: number | "register") => {
    if (!companyId) {
      return;
    }

    navigate(getItemMasterManagePath(companyId, String(id)));
  }, [companyId, getItemMasterManagePath, navigate]);

  const deleteItem = useCallback(async (id: number) => {
    const response = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/company-item-masters/${id}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(t("itemMaster.messages.notFound"));
      }
      const message = await parseApiErrorMessage(response);
      throw new Error(message ?? t("itemMaster.deleteFailed"));
    }
  }, [parseApiErrorMessage, t]);

  const onDeleteItem = useCallback(async (id: number) => {
    try {
      await deleteItem(id);

      const currentPageSize = pagination.pageSize;
      const totalAfterDelete = Math.max(pagination.total - 1, 0);
      const maxPage = Math.max(1, Math.ceil(totalAfterDelete / currentPageSize));
      const targetPage = Math.min(pagination.current, maxPage);
      const apiSortOrder = toApiSortOrder(currentSortOrder);

      await fetchItemMasters({
        page: targetPage,
        pageSize: currentPageSize,
        sortField: currentSortField,
        sortOrder: apiSortOrder,
        filters: currentFilters,
      });
      modal.success({ content: t("itemMaster.deleteSucceeded"), centered: true });
    } catch (error) {
      console.error("Error deleting company item master.", error);
      modal.error({
        content: error instanceof Error ? error.message : t("itemMaster.deleteFailed"),
        centered: true,
      });
    }
  }, [currentFilters, currentSortField, currentSortOrder, deleteItem, fetchItemMasters, modal, pagination, t, toApiSortOrder]);

  const onUploadCsv = useCallback(async (options: Parameters<NonNullable<UploadProps["customRequest"]>>[0]) => {
    const { file, onSuccess, onError } = options;
    const fileName = typeof file === "object" && file !== null && "name" in file && typeof file.name === "string"
      ? file.name
      : "company_item_masters.csv";

    if (!companyId) {
      onError?.(new Error("companyId is required"));
      return;
    }

    setIsUploading(true);

    const formData = new FormData();
    formData.append("file", file as Blob, fileName);

    try {
      const response = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/csv/upload/jan-codes/${companyId}`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        const result = await response.json() as { created?: number; updated?: number };
        await fetchItemMasters({
          page: pagination.current,
          pageSize: pagination.pageSize,
          sortField: currentSortField,
          sortOrder: toApiSortOrder(currentSortOrder),
          filters: currentFilters,
        });
        modal.success({
          content: t("itemMaster.uploadSucceededWithCounts", {
            fileName,
            created: result.created ?? 0,
            updated: result.updated ?? 0,
          }),
          centered: true,
        });
        onSuccess?.(result);
        return;
      }

      const payload = await parseItemMasterCsvErrorPayload(response);
      showItemMasterCsvUploadErrorModal(fileName, payload);
      onError?.(new Error(t("itemMaster.uploadFailed", { fileName })));
    } catch (error) {
      console.error("Error uploading company item master CSV.", error);
      showItemMasterCsvUploadErrorModal(fileName);
      onError?.(error as Error);
    } finally {
      setIsUploading(false);
    }
  }, [companyId, currentFilters, currentSortField, currentSortOrder, fetchItemMasters, modal, pagination, t, toApiSortOrder]);

  const onDownloadCsv = useCallback(async () => {
    if (!companyId) {
      modal.error({ content: t("itemMaster.companyIdMissing"), centered: true });
      return;
    }

    const extractFileName = (contentDispositionHeader?: string | null) => {
      if (!contentDispositionHeader) {
        return undefined;
      }

      try {
        const parsed = parseContentDisposition(contentDispositionHeader);
        const filenameStar = typeof parsed.parameters?.["filename*"] === "string" ? parsed.parameters["filename*"] : undefined;
        if (filenameStar) {
          return filenameStar;
        }

        const rawName = typeof parsed.parameters?.filename === "string" ? parsed.parameters.filename : undefined;
        if (!rawName) {
          return undefined;
        }

        if (rawName.includes("%")) {
          try {
            return decodeURIComponent(rawName);
          } catch (_error) {
            return rawName;
          }
        }

        return rawName;
      } catch (_error) {
        return undefined;
      }
    };

    setIsDownloadingCsv(true);
    try {
      const response = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/csv/download/jan-codes/${companyId}`, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error("Failed to download company item master CSV");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response.headers.get("Content-Disposition");
      link.href = url;
      link.setAttribute("download", extractFileName(disposition) ?? `company_item_masters_${companyId}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading company item master CSV.", error);
      modal.error({ content: t("messages.csvDownloadFailed"), centered: true });
    } finally {
      setIsDownloadingCsv(false);
    }
  }, [companyId, modal, t]);

  const janCodeSearchValidation = validateCompanyItemMasterJanCodeSearch(searchForm);
  const isJanCodeSearchInvalid = !janCodeSearchValidation.isValid;
  const janCodeSearchHelpText = janCodeSearchValidation.messageKey
    ? t(janCodeSearchValidation.messageKey, janCodeSearchValidation.messageValues)
    : t("itemMaster.searchHelp");

  if (!isPageReady) {
    return (
      <Flex justify="center" align="center" className="min-h-[50vh]">
        <Spin size="large" tip={t("common.loadingWithDots")} />
      </Flex>
    );
  }

  return (
    <div>
      <Spin spinning={isUploading} tip={t("common.csvUploading")} size="large" fullscreen />
      <div className="flex w-full space-x-5 items-center">
        <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2 break-all">
          {selectedCompanyName ? t("itemMaster.companyListPage", { companyName: selectedCompanyName }) : t("itemMaster.listPage")}
        </p>
      </div>
      <Divider />
      <div className="mb-5 overflow-hidden rounded-xl border border-[#d6e4ff] bg-white shadow-sm">
        <div className="bg-[#f0f5ff] px-4 py-4">
          <Flex align="center" justify="space-between" gap="middle" wrap>
            <Flex align="center" gap="middle" className="min-w-0">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="m-0 text-base font-semibold leading-none text-[#1f1f1f]">{t("itemMaster.targetCompanySelect")}</p>
                  <span className="inline-flex h-6 items-center rounded-full bg-[#1677ff] px-2 text-xs font-medium leading-none text-white">{t("itemMaster.required")}</span>
                </div>
                <p className="mt-1 mb-0 text-xs text-[#595959]">
                  {t("itemMaster.companySelectDescription")}
                </p>
              </div>
            </Flex>
            <Select
              allowClear
              size="large"
              value={companyId ?? undefined}
              loading={isLoadingCompanies}
              options={companySelectOptions}
              placeholder={t("itemMaster.selectCompany")}
              className="w-full text-start md:w-[440px]"
              onChange={handleCompanyChange}
            />
          </Flex>
        </div>
      </div>
      <div className="mb-4 rounded-md border border-[#d9d9d9] bg-[#fafafa] px-4 py-3">
        <Flex vertical gap="middle">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5">
            <div className="min-w-0 md:col-span-2">
              <p className="mb-1 text-xs font-medium text-[#595959]">{t("itemMaster.janCode")}</p>
              <Flex gap="small" wrap={false}>
                <Input
                  placeholder={t("itemMaster.janCodeInput")}
                  value={searchForm.jan_code}
                  allowClear
                  disabled={!companyId}
                  status={isJanCodeSearchInvalid ? "error" : undefined}
                  className="min-w-0 flex-1"
                  onChange={(event) => handleJanCodeInputChange(event.target.value)}
                  onPressEnter={() => {
                    void onSearch();
                  }}
                />
                <Select
                  value={searchForm.jan_code_match_type}
                  disabled={!companyId}
                  options={COMPANY_ITEM_MASTER_JAN_CODE_MATCH_TYPE_OPTIONS.map((option) => ({
                    value: option.value,
                    label: t(option.labelKey),
                  }))}
                  className="w-[128px] shrink-0 text-start"
                  onChange={handleJanCodeMatchTypeChange}
                />
              </Flex>
              <p className={`mt-1 mb-0 text-xs ${isJanCodeSearchInvalid ? "text-[#ff4d4f]" : "text-[#8c8c8c]"}`}>
                {janCodeSearchHelpText}
              </p>
            </div>
            {ITEM_MASTER_SEARCH_FIELDS.map((field) => (
              <div key={field.key} className="min-w-0">
                <p className="mb-1 text-xs font-medium text-[#595959]">{t(field.labelKey)}</p>
                <Input
                  placeholder={t(field.placeholderKey)}
                  value={searchForm[field.key]}
                  allowClear
                  disabled={!companyId}
                  maxLength={COMPANY_ITEM_MASTER_MAX_TEXT_LENGTH}
                  onChange={(event) => handleSearchInputChange(field.key, event.target.value)}
                  onPressEnter={() => {
                    void onSearch();
                  }}
                />
              </div>
            ))}
          </div>
          <Flex justify="end" gap="small" wrap>
            <Button disabled={!companyId} onClick={() => {
              void onClearSearch();
            }}
            >
              {t("itemMaster.clear")}
            </Button>
            <Button type="primary" disabled={!companyId || isJanCodeSearchInvalid} onClick={() => {
              void onSearch();
            }}
            >
              {t("common.search")}
            </Button>
          </Flex>
        </Flex>
      </div>
      <Flex className="my-2 flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
        <Flex align="center" className="order-2 ml-3 lg:order-1">
          <Badge status={companyId ? "processing" : "default"} color={companyId ? "blue" : undefined} />
          <span className="pl-1">
            {companyId
              ? t("itemMaster.totalCount", { count: pagination.total })
              : t("itemMaster.companyNotSelected")}
          </span>
        </Flex>
        <Flex gap="small" wrap className="order-1 w-full justify-stretch sm:justify-end lg:order-2 lg:w-auto">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => onClickManage("register")}
            disabled={!companyId}
            className="w-full sm:w-auto"
          >
            {t("itemMaster.registerItem")}
          </Button>
          <CsvImportModalButton
            title={t("csvImportTitles.itemMaster")}
            templateUrl={`${process.env.PUBLIC_URL}/csv-templates/company_item_masters.csv`}
            templateFileName="company_item_masters_template.csv"
            guideUrl={`${process.env.PUBLIC_URL}/csv-guides/company-item-master-import.md`}
            uploadRequest={onUploadCsv}
            disabled={!companyId || isUploading}
            uploading={isUploading}
            buttonText={t("itemMaster.csvBulkRegister")}
            className="w-full sm:w-auto"
            buttonClassName="w-full sm:w-auto"
          />
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={onDownloadCsv}
            loading={isDownloadingCsv}
            disabled={!companyId}
            className="w-full sm:w-auto"
          >
            {t("common.downloadCsv")}
          </Button>
        </Flex>
      </Flex>
      <Flex vertical justify="center" className="relative">
        <Table<CompanyItemMasterResponseType>
          dataSource={itemMasterList}
          loading={isLoading}
          bordered
          locale={{
            emptyText: companyId
              ? t("common.noData")
              : t("itemMaster.selectCompanyToDisplayItems"),
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            position: ["bottomCenter"],
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (totalCount, range) => t("itemMaster.showTotal", { total: totalCount, start: range[0], end: range[1] }),
            pageSizeOptions: COMPANY_ITEM_MASTER_PAGE_SIZE_OPTIONS,
          }}
          onChange={handleTableChange}
          rowKey={(record) => record.id}
          scroll={{ x: "max-content" }}
        >
          <Column
            title={t("itemMaster.janCode")}
            dataIndex="jan_code"
            key="jan_code"
            sorter
            sortOrder={currentSortField === "jan_code" ? currentSortOrder : undefined}
            render={(value: CompanyItemMasterResponseType["jan_code"]) => getCompanyItemMasterDisplayValue(value)}
          />
          <Column
            title={t("itemMaster.repeat")}
            dataIndex="repeat"
            key="repeat"
            sorter
            sortOrder={currentSortField === "repeat" ? currentSortOrder : undefined}
            render={(value: CompanyItemMasterResponseType["repeat"]) => getCompanyItemMasterDisplayValue(value)}
          />
          <Column
            title={t("itemMaster.partNumber")}
            dataIndex="part_number"
            key="part_number"
            sorter
            sortOrder={currentSortField === "part_number" ? currentSortOrder : undefined}
            render={(value: CompanyItemMasterResponseType["part_number"]) => getCompanyItemMasterDisplayValue(value)}
          />
          <Column
            title={t("itemMaster.sampleNumber")}
            dataIndex="sample_number"
            key="sample_number"
            sorter
            sortOrder={currentSortField === "sample_number" ? currentSortOrder : undefined}
            render={(value: CompanyItemMasterResponseType["sample_number"]) => getCompanyItemMasterDisplayValue(value)}
          />
          <Column
            title={t("itemMaster.itemName")}
            dataIndex="item_name"
            key="item_name"
            sorter
            sortOrder={currentSortField === "item_name" ? currentSortOrder : undefined}
            render={(value: CompanyItemMasterResponseType["item_name"]) => getCompanyItemMasterDisplayValue(value)}
          />
          <Column
            title={t("itemMaster.brandName")}
            dataIndex="brand_name"
            key="brand_name"
            sorter
            sortOrder={currentSortField === "brand_name" ? currentSortOrder : undefined}
            render={(value: CompanyItemMasterResponseType["brand_name"]) => getCompanyItemMasterDisplayValue(value)}
          />
          <Column
            title={t("itemMaster.size")}
            dataIndex="size"
            key="size"
            sorter
            sortOrder={currentSortField === "size" ? currentSortOrder : undefined}
            render={(value: CompanyItemMasterResponseType["size"]) => getCompanyItemMasterDisplayValue(value)}
          />
          <Column
            title={t("itemMaster.colorNo")}
            dataIndex="color_no"
            key="color_no"
            render={(value: CompanyItemMasterResponseType["color_no"]) => getCompanyItemMasterDisplayValue(value)}
          />
          <Column
            title={t("itemMaster.createdAt")}
            dataIndex="created_at"
            key="created_at"
            sorter
            sortOrder={currentSortField === "created_at" ? currentSortOrder : undefined}
            render={(value: CompanyItemMasterResponseType["created_at"]) => renderDateTime(value)}
          />
          <Column
            title={t("itemMaster.updatedAt")}
            dataIndex="updated_at"
            key="updated_at"
            sorter
            sortOrder={currentSortField === "updated_at" ? currentSortOrder : undefined}
            render={(value: CompanyItemMasterResponseType["updated_at"]) => renderDateTime(value)}
          />
          <Column
            title={t("common.operation")}
            dataIndex="action"
            key="action"
            fixed="right"
            render={(_: unknown, record: CompanyItemMasterResponseType) => (
              <Space size="middle">
                <Button variant="outlined" color="primary" onClick={() => onClickManage(record.id)}>{t("common.edit")}</Button>
                <Button
                  variant="outlined"
                  color="danger"
                  onClick={() => {
                    modal.warning({
                      title: t("itemMaster.deleteTarget", { name: record.item_name ?? record.jan_code ?? t("itemMaster.fallbackDeleteTarget") }),
                      icon: <ExclamationCircleFilled />,
                      content: t("itemMaster.deleteConfirm"),
                      centered: true,
                      cancelText: t("common.cancel"),
                      okText: t("itemMaster.deleteOk"),
                      okType: "danger",
                      okCancel: true,
                      async onOk() {
                        await onDeleteItem(record.id);
                      },
                    });
                  }}
                >
                  {t("common.delete")}
                </Button>
              </Space>
            )}
          />
        </Table>
      </Flex>
    </div>
  );
}
